import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/AuthProviderUnified';
import { isDashboardEnabled, dashboardRoutes } from '../config/featureFlags';
import logger from '../utils/logger.js';
import DarkSuspenseFallback from './DarkSuspenseFallback';

// Helper function to get appropriate dashboard route based on role and feature flags
const getDashboardRoute = (userRole) => {
  if (!userRole) return '/'; // No role, go to landing

  // Map user roles to dashboard types
  // Map canonical user roles to dashboard types
  const roleToDashboard = {
    gcAdmin: 'gc',
    contractor: 'contractor',         // Backend role
    scAdmin: 'sc',
    subcontractor: 'subcontractor',   // Backend role
    admin: 'admin',
    'org-admin': 'orgAdmin',
    broker: 'broker',
    underwriter: 'underwriter',
    industrial: 'industrial',
    industrialAdmin: 'industrial',
    'industrial-admin': 'industrial',
    // Individual account types with dashboard access
    foreman: 'contractor',
    safety_supervisor: 'contractor',
    worker: 'worker',
  };

  const dashboardType = roleToDashboard[userRole];

  // Check if the dashboard is enabled
  if (dashboardType && isDashboardEnabled(dashboardType)) {
    return dashboardRoutes[dashboardType];
  }

  // If user's dashboard is disabled, redirect to landing page with message
  logger.warn(`Dashboard for role '${userRole}' is currently disabled`);
  return '/';
};

function RequireAuth({ children, role }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleGoBack = useCallback(() => {
    navigate('/');
  }, [navigate]);

  // Show loading state — uses theme-aware fallback to prevent white flash
  if (loading) {
    return <DarkSuspenseFallback />;
  }

  // Require authentication
  if (!user) {
    logger.info('No user found, redirecting to login');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Normalize role to array for consistent handling
  const requiredRoles = role ? (Array.isArray(role) ? role : [role]) : [];

  // Check if the required role's dashboard is disabled
  if (requiredRoles.length > 0) {
    const roleToDashboard = {
      gcAdmin: 'gc',
      contractor: 'contractor',         // Backend role
      scAdmin: 'sc',
      subcontractor: 'subcontractor',   // Backend role
      admin: 'admin',
      'org-admin': 'orgAdmin',
      broker: 'broker',
      underwriter: 'underwriter',
      industrial: 'industrial',
      industrialAdmin: 'industrial',
      'industrial-admin': 'industrial',
      // Individual account types with dashboard access
      foreman: 'contractor',
      safety_supervisor: 'contractor',
      worker: 'worker',
    };

    // Find the first applicable dashboard type from the roles array
    let requiredDashboard = null;
    for (const r of requiredRoles) {
      const dashboardType = roleToDashboard[r];
      if (dashboardType) {
        requiredDashboard = dashboardType;
        break;
      }
    }

    if (requiredDashboard && !isDashboardEnabled(requiredDashboard)) {
      logger.warn(`Access denied: ${requiredDashboard} dashboard is currently disabled`);
      return (
        <div
          className="min-h-screen flex items-center justify-center"
          style={{ backgroundColor: 'var(--color-background-primary)', color: 'var(--color-text-primary)' }}
        >
          <div className="text-center p-8">
            <h2
              className="text-2xl font-bold mb-4"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Dashboard Temporarily Unavailable
            </h2>
            <p className="mb-4" style={{ color: 'var(--color-text-secondary)' }}>
              The {requiredDashboard.toUpperCase()} dashboard is currently disabled for maintenance.
            </p>
            <p className="text-sm" style={{ color: 'var(--color-text-tertiary, var(--color-text-secondary))' }}>
              Please contact support if you need immediate access.
            </p>
            <button
              onClick={handleGoBack}
              className="mt-4 px-6 py-2 rounded-full font-bold transition-colors"
              style={{ backgroundColor: 'var(--color-primary-main)', color: '#fff' }}
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }
  }

  // Require specific role - check if user's role is in the required roles array
  if (requiredRoles.length > 0 && !requiredRoles.includes(user.role)) {
    // Special case: gcAdmin can access admin routes
    if (requiredRoles.includes('admin') && user.role === 'gcAdmin') {
      return children;
    }
    logger.info('Role mismatch:', { context: { required: requiredRoles, actual: user.role } });
    // Redirect to the correct dashboard for their role (if enabled)
    const userDashboardRoute = getDashboardRoute(user.role);
    return <Navigate to={userDashboardRoute} replace />;
  }

  return children;
}

RequireAuth.propTypes = {
  children: PropTypes.node.isRequired,
  role: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
};

export default RequireAuth;
